<template>
  <PrmButton
    v-tooltip="shownPinned ? unpinTooltip : pinTooltip"
    type="button"
    text
    severity="secondary"
    data-qa="pin-toggle-button"
    :aria-pressed="shownPinned"
    :aria-disabled="disabled"
    @mouseenter="isHovered = true"
    @mouseleave="onMouseleave()"
    @click.stop="onClick()"
  >
    <template #icon>
      <component
        :is="pinIcon"
        class="pin-toggle-button__icon"
        data-qa="pin-toggle-button__icon"
        :data-qa-id="pinIconId"
      />
    </template>
  </PrmButton>
</template>

<script lang="ts" setup>
import PrmButton from '@NitrexGroup/primevue/button';
import {
  computed,
  ref,
  watch,
} from 'vue';

import PinPulledIcon from '@/assets/icon/pin-pulled.svg?component';
import PinPushedIcon from '@/assets/icon/pin-pushed.svg?component';
import PinRemoveIcon from '@/assets/icon/pin-remove.svg?component';

const props = defineProps<{
  pinned: boolean,
  pinTooltip: string,
  unpinTooltip: string,
  disabled?: boolean,
}>();

const emit = defineEmits<{
  (event: 'update:pinned', pinned: boolean): void,
}>();

const isHovered = ref(false);
// Hovering previews the state a click leads to. Right after a click the pointer is still on the
// button, so the preview would show the opposite of what just happened until the pointer leaves.
const wasToggledDuringHover = ref(false);

const showsClickPreview = computed(() => isHovered.value && !wasToggledDuringHover.value);

// The pinned state only changes once the server has stored it. Until then, the button shows the
// state the click requested instead of the old one.
const requestedPinned = ref<boolean>();
const shownPinned = computed(() => requestedPinned.value ?? props.pinned);

watch(
  () => props.pinned,
  () => {
    requestedPinned.value = undefined;
  },
);

// A failed request leaves the pinned state unchanged; the button falls back to it once the
// request has settled.
watch(
  () => props.disabled,
  (isDisabled) => {
    if (!isDisabled) {
      requestedPinned.value = undefined;
    }
  },
);

const pinIcon = computed(() => {
  if (shownPinned.value) {
    return showsClickPreview.value ? PinRemoveIcon : PinPushedIcon;
  }
  return showsClickPreview.value ? PinPushedIcon : PinPulledIcon;
});

const pinIconId = computed(() => {
  if (pinIcon.value === PinRemoveIcon) {
    return 'remove';
  }
  if (pinIcon.value === PinPushedIcon) {
    return 'pushed';
  }
  return 'pulled';
});

// The button is not disabled natively while a request runs: a disabled button no longer receives
// pointer events, so the browser reports a mouseleave, the hover preview restarts and replaces the
// new state as soon as the request has settled.
function onClick() {
  if (props.disabled) {
    return;
  }
  wasToggledDuringHover.value = true;
  requestedPinned.value = !shownPinned.value;
  emit('update:pinned', requestedPinned.value);
}

function onMouseleave() {
  isHovered.value = false;
  wasToggledDuringHover.value = false;
}
</script>

<style lang="scss" scoped>
.pin-toggle-button {
  &__icon {
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
